import { useMemo } from 'react';

import { getRouteApi } from '@tanstack/react-router';
import { APPLICANT_ATTRIBUTION_BOTH } from 'cpool-constants';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { DispositionsKredit } from './DispositionsKredit';
import { GeschaeftsKredit } from './GeschaeftsKredit';
import { Immobiliendarlehen } from './Immobiliendarlehen';
import { KontokorrentKredit } from './KontokorrentKredit';
import { KreditKarte } from './KreditKarte';
import { Leasing } from './Leasing';
import { RatenKredit } from './RatenKredit';
import { SonstigeVerbindlichkeit } from './SonstigeVerbindlichkeit';
import useLead from '../../../../../hooks/useLead';
import type { SchemaCreditRequestForm } from '../../schemas/creditRequestForm.schema';
import { LoanTypeKeys } from '../../types';
import type { Credit, LoanType } from '../../types';
import { useChangeLiabilityHideState } from '../hooks/useChangeLiabilityHideState';
import { useExistingLoanActions } from '../hooks/useExistingLoanActions';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const LOAN_COMPONENT_MAP: Record<LoanType, React.FC<any>> = {
    dispositionskredite: DispositionsKredit,
    geschaeftskredite: GeschaeftsKredit,
    immobiliendarlehen: Immobiliendarlehen,
    kontokorrentkredite: KontokorrentKredit,
    kreditkarten: KreditKarte,
    leasings: Leasing,
    ratenkredite: RatenKredit,
    sonstigeVerbindlichkeiten: SonstigeVerbindlichkeit,
};

export const CreditList = () => {
    const { useParams } = getRouteApi('/_app/leads_/details/$leadId');
    const { leadId } = useParams();
    const { data: lead } = useLead(leadId);
    const { t } = useTranslation();
    const { getValues, reset, setValue, watch } = useFormContext<SchemaCreditRequestForm>();
    const watchedLoanTypes = watch(LoanTypeKeys);
    const { mutate: changeLiabilityHideState } = useChangeLiabilityHideState({ loanRequestId: leadId });

    const {
        changeMutation: { mutate: changeExistingLoan },
    } = useExistingLoanActions({
        loanRequestId: leadId,
    });

    const toggleHide = (loanType: LoanType, index: number) => {
        const currentValues = getValues(loanType) || [];
        const credit = currentValues[index];

        if (credit && credit.id) {
            setValue(`${loanType}.${index}`, { ...credit, hide: !credit.hide });
            changeLiabilityHideState({ hideState: !credit.hide, id: credit.id });
        }

        reset(getValues(), { keepDirty: false, keepValues: true });
    };

    const toggleRefinance = (loanType: LoanType, index: number) => {
        const currentValues = getValues(loanType) || [];
        const credit = currentValues[index];

        if (credit && 'abloesen' in credit) {
            setValue(`${loanType}.${index}`, { ...credit, abloesen: !credit.abloesen });
            changeExistingLoan({ loanType, payload: { ...credit, abloesen: !credit.abloesen } });
        }
    };

    const creditItems = useMemo(() => {
        const arr: Array<Credit & { index: number; loanType: LoanType }> = [];

        // eslint-disable-next-line no-restricted-syntax
        for (const loanType of LoanTypeKeys) {
            const currentValues = watch(loanType) || [];

            currentValues.forEach((credit, index) => {
                arr.push({ ...credit, index, loanType });
            });
        }

        arr.sort((a, b) => {
            if (!a.createdAt && !b.createdAt) return 0;
            if (!a.createdAt) return 1;
            if (!b.createdAt) return -1;
            const dateA = new Date(a.createdAt).getTime();
            const dateB = new Date(b.createdAt).getTime();
            return dateB - dateA;
        });

        return arr;
    }, [LoanTypeKeys, watchedLoanTypes]);

    return (
        <>
            {creditItems.map((credit) => {
                const Component = LOAN_COMPONENT_MAP[credit.loanType as LoanType];
                return (
                    <Component
                        key={`${credit.index}-${credit.loanType}`}
                        credit={credit}
                        index={credit.index}
                        name={credit.loanType}
                        toggleHide={() => {
                            if (
                                lead?.coApplicantHidden &&
                                credit.gehoertZuAntragsteller === APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_2
                            ) {
                                toast.warn(t('CREDIT_WORTHINESS.TOASTS.CO_APPLICANT_HIDDEN'));
                                return;
                            }
                            toggleHide(credit.loanType, credit.index);
                        }}
                        toggleRefinance={() => toggleRefinance(credit.loanType, credit.index)}
                    />
                );
            })}
        </>
    );
};
